import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';

import { auth, firestore } from '../firebase';
import { displayNameOf, userSchema } from '../../shared/schemas';
import { useAppState } from '../lib/appState';
import { usePunFeed } from '../lib/usePunFeed';
import PunCard from './PunCard';

/**
 * Everything that is not writing a pun or reading the feed.
 *
 * These used to be scattered across a settings dropdown that held My Puns and
 * How It Works (neither of which is a setting), a Manage tab, and a separate
 * /friends route that duplicated the tab. One page, one scroll.
 */
function You() {
  const navigate = useNavigate();
  const { userId, crownCount } = useAppState();
  const [name, setName] = useState('');

  const { puns, names, loading, crownSpent, react } = usePunFeed({
    scope: 'user',
    viewerId: userId,
    targetUserId: userId,
    // Your whole history, newest first.
    range: { kind: 'all' },
  });

  useEffect(() => {
    let cancelled = false;

    getDoc(doc(firestore, 'users', userId))
      .then((snapshot) => {
        if (cancelled || !snapshot.exists()) return;
        const parsed = userSchema.safeParse({ ...snapshot.data(), id: snapshot.id });
        if (parsed.success) setName(displayNameOf(parsed.data));
      })
      .catch((error) => console.error('[You] could not load profile:', error));

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleSignOut = async () => {
    await auth.signOut();
    navigate('/');
  };

  return (
    <div>
      <section className="rounded-2xl border-2 border-ink bg-white px-6 py-7 text-center shadow-pop">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-ink bg-grape font-display text-2xl font-extrabold text-white">
          {(name || '?').charAt(0).toUpperCase()}
        </div>

        <p className="font-display text-2xl font-bold">{name || 'Loading...'}</p>

        <div className="mt-5 flex justify-center gap-3">
          <Stat value={crownCount} label={crownCount === 1 ? 'crown' : 'crowns'} />
          <Stat value={puns.length} label={puns.length === 1 ? 'pun' : 'puns'} />
        </div>
      </section>

      <nav className="mt-6 space-y-3">
        <Row to="/friends">Friends</Row>
        <Row to="/rules">How it works</Row>
        <Row to="/settings">Settings</Row>
      </nav>

      <h2 className="mb-4 mt-10 font-display text-xl font-bold">Your puns</h2>

      {loading ? (
        <div className="flex justify-center py-12">
          <span className="h-8 w-8 animate-spin rounded-full border-[3px] border-ink/15 border-t-ink" />
        </div>
      ) : puns.length === 0 ? (
        <p className="rounded-2xl border-2 border-dashed border-ink/30 bg-white/60 px-6 py-10 text-center text-sm text-ink/60">
          No puns yet. Your greatest hits are pending.
        </p>
      ) : (
        <div className="space-y-5">
          {puns.map((pun) => (
            <PunCard
              key={pun.id}
              pun={pun}
              authorName={names[pun.userId] ?? name ?? 'You'}
              viewerId={userId}
              crownSpent={crownSpent}
              onReact={(reaction) => react(pun, reaction)}
            />
          ))}
        </div>
      )}

      <button
        onClick={handleSignOut}
        className="press mt-12 w-full rounded-xl border-2 border-ink bg-coral px-5 py-3 font-display font-bold text-white shadow-pop"
      >
        Sign out
      </button>
    </div>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <span className="rounded-xl border-2 border-ink bg-mustard px-4 py-2 font-display text-lg font-bold tabular-nums shadow-pop-sm">
      {value} <span className="text-sm font-semibold">{label}</span>
    </span>
  );
}

function Row({ to, children }: { to: string; children: string }) {
  return (
    <Link
      to={to}
      className="press flex items-center justify-between rounded-xl border-2 border-ink bg-white px-5 py-4 font-display font-bold shadow-pop-sm hover:bg-mustard/20"
    >
      {children}
      <svg
        className="h-4 w-4"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );
}

export default You;
